import { defineStore } from 'pinia';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import { DocumentEndPoint } from '@/modules/document/enums/document-end-point.enum.ts';
import { wrapSpaces } from '@/modules/common/utils/wrap-spaces.util.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import { GatewayFilter } from '@/modules/core/utils/gateway-filter.ts';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';

type RawDocumentRowResponse = { data: ItemsDto<Record<string, unknown>> };

type DocumentRowState = {
  item: Record<string, unknown>;
  isLoading: boolean;
  controller: AbortController | null;
};

export const useDocumentRowStore = defineStore(StoreId.DocumentRow, {
  state: (): DocumentRowState => ({
    item: {},
    isLoading: false,
    controller: null,
  }),
  actions: {
    async fetch(docId: number) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.isLoading = true;

      try {
        const response = await GatewayApi.execute<RawDocumentRowResponse>(DocumentEndPoint.GetList, {
          filter: GatewayFilter.and(GatewayFilter.eq('DOC_ID', docId)),
          limit: 1,
          signal: controller.signal,
          mapData: false,
        });

        if (this.controller !== controller) {
          return;
        }

        const item: Record<string, unknown> = response.data.items[0] || {};

        if (typeof item['Комментарий'] === 'string' && item['Комментарий']) {
          item['Комментарий'] = wrapSpaces(item['Комментарий']);
        }

        this.item = item;
      } catch (error) {
        if (this.controller !== controller) {
          return;
        }
        if (ErrorUtil.isForbidden(error) || ErrorUtil.isUnexpected(error)) {
          throw error;
        }
      } finally {
        if (this.controller === controller) {
          this.isLoading = false;
          this.controller = null;
        }
      }
    },
    async fetchOnly(docId: number): Promise<Record<string, unknown>> {
      const item = await GatewayApi.getOne<Record<string, unknown>>(
        DocumentEndPoint.GetList,
        GatewayFilter.and(GatewayFilter.eq('DOC_ID', docId))!
      );

      if (!item) {
        throw new Error('Entity not found');
      }

      return item;
    },
    abort() {
      this.controller?.abort();
      this.isLoading = false;
      this.controller = null;
    },
    clear() {
      this.item = {};
    },
  },
});
